import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * AsyncSelect component for web
 *
 * Searchable dropdown that fetches options asynchronously from an API
 * Supports debouncing to reduce API calls and loading states
 */
const AsyncSelect = ({
  label,
  placeholder = 'Search...',
  value,
  onChange,
  loadOptions,
  error,
  disabled = false,
  required = false,
  allowClear = true,
  className = '',
  id,
  name,
  debounceMs = 300,
  minSearchLength = 0,
  noOptionsMessage = 'No options found',
  loadingMessage = 'Loading...',
  formatOptionLabel = null,
  getOptionValue = (option) => option.value,
  getOptionLabel = (option) => option.label,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Generate a safe ID
  const selectId = id || name || (typeof label === 'string' ? `async-select-${label.replace(/\s+/g, '-').toLowerCase()}` : 'async-select');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus search input when opened
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Debounced load options
  const debouncedLoadOptions = useCallback(
    (search) => {
      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Don't search if below minimum length
      if (search.length < minSearchLength) {
        setOptions([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      // Set new timer
      debounceTimerRef.current = setTimeout(async () => {
        try {
          const results = await loadOptions(search);
          setOptions(results || []);
        } catch (err) {
          console.error('Error loading options:', err);
          setOptions([]);
        } finally {
          setIsLoading(false);
        }
      }, debounceMs);
    },
    [loadOptions, debounceMs, minSearchLength]
  );

  // Load options when search term changes
  useEffect(() => {
    if (isOpen) {
      debouncedLoadOptions(searchTerm);
    }

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm, isOpen, debouncedLoadOptions]);

  // Load initial options when opened
  useEffect(() => {
    if (isOpen && searchTerm === '' && minSearchLength === 0) {
      debouncedLoadOptions('');
    }
  }, [isOpen, debouncedLoadOptions, searchTerm, minSearchLength]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  const handleSelect = (option) => {
    const optionValue = getOptionValue(option);
    onChange?.(optionValue);
    setSelectedOption(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.('');
    setSelectedOption(null);
  };

  const getDisplayValue = () => {
    if (selectedOption) {
      return getOptionLabel(selectedOption);
    }
    // If we have a value but no selected option, try to find it in current options
    if (value) {
      const option = options.find((opt) => getOptionValue(opt) === value);
      if (option) {
        return getOptionLabel(option);
      }
    }
    return placeholder;
  };

  const isSelected = (option) => {
    return getOptionValue(option) === value;
  };

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label htmlFor={selectId} className="label">
          {label}
          {required && <span className="text-error-600 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <div
          onClick={handleToggle}
          className={[
            'input text-left flex items-center justify-between',
            error && 'input-error',
            disabled && 'opacity-50 cursor-not-allowed',
            !disabled && 'cursor-pointer',
            className,
          ].filter(Boolean).join(' ')}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          id={selectId}
          onKeyDown={(e) => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              handleToggle();
            }
          }}
          {...props}
        >
          <span className={!value ? 'text-neutral-400' : ''}>
            {getDisplayValue()}
          </span>
          <div className="flex items-center gap-1">
            {allowClear && value && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="text-neutral-400 hover:text-neutral-600 transition-colors"
                aria-label="Clear selection"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <svg
              className={`w-5 h-5 text-neutral-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-dropdown max-h-80 overflow-hidden">
            {/* Search Input */}
            <div className="p-2 border-b border-neutral-200">
              <input
                ref={searchInputRef}
                type="text"
                placeholder={`${placeholder}${minSearchLength > 0 ? ` (min ${minSearchLength} characters)` : ''}`}
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full px-3 py-2 text-sm border border-neutral-200 rounded focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {/* Options List */}
            <div className="overflow-y-auto max-h-64 scrollbar-thin" role="listbox">
              {isLoading ? (
                <div className="px-4 py-6 text-sm text-center text-neutral-500">
                  {loadingMessage}
                </div>
              ) : searchTerm.length < minSearchLength ? (
                <div className="px-4 py-6 text-sm text-center text-neutral-500">
                  Type at least {minSearchLength} character{minSearchLength !== 1 ? 's' : ''} to search
                </div>
              ) : options.length > 0 ? (
                options.map((option, index) => (
                  <button
                    key={getOptionValue(option) || index}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={[
                      'w-full text-left px-4 py-2 text-sm hover:bg-neutral-50 transition-colors flex items-center justify-between',
                      isSelected(option) && 'bg-primary-50 text-primary-700 font-medium',
                    ].filter(Boolean).join(' ')}
                    role="option"
                    aria-selected={isSelected(option)}
                  >
                    {formatOptionLabel ? formatOptionLabel(option) : (
                      <span>{getOptionLabel(option)}</span>
                    )}
                    {isSelected(option) && (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))
              ) : (
                <div className="px-4 py-6 text-sm text-center text-neutral-500">
                  {noOptionsMessage}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && (
        <p id={`${selectId}-error`} className="mt-1 text-sm text-error-600">
          {error}
        </p>
      )}
    </div>
  );
};

AsyncSelect.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onChange: PropTypes.func,
  loadOptions: PropTypes.func.isRequired,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  allowClear: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  debounceMs: PropTypes.number,
  minSearchLength: PropTypes.number,
  noOptionsMessage: PropTypes.string,
  loadingMessage: PropTypes.string,
  formatOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  getOptionLabel: PropTypes.func,
};

export default AsyncSelect;
